import type { RatingStarProps } from './RatingStar.tsx';
import clsx from 'clsx';
import { formatDecimalPlaces } from '@_/utils';
import { RatingStar } from './RatingStar.tsx';

interface RatingProps {
  color?: 'default' | 'primary';
  condensed?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  weight?: 'normal' | 'semibold' | 'bold';
  value: number;
}

export function Rating({
  color = 'default',
  condensed = false,
  size = 'sm',
  weight = 'semibold',
  value
}: RatingProps) {
  const starStyle = clsx(
    color === 'default' && 'text-gray-800',
    color === 'primary' && 'text-primary-700',
    size === 'xs' && 'size-3',
    size === 'sm' && 'size-4',
    size === 'md' && 'size-5',
    size === 'lg' && 'size-5',
    size === 'xl' && 'size-5',
    size === '2xl' && 'size-6'
  );

  if (condensed) {
    return (
      <div
        className={clsx(
          'flex items-center gap-x-[0.16em]',
          size === 'xs' && 'text-xs',
          size === 'sm' && 'text-sm',
          size === 'md' && 'text-base',
          size === 'lg' && 'text-lg',
          size === 'xl' && 'text-xl',
          size === '2xl' && 'text-2xl'
        )}
      >
        <div aria-hidden="true">
          <RatingStar className={clsx(starStyle, '-mt-0.5')} />
        </div>
        {value > 0 && (
          <div
            className={clsx(
              'text-gray-800',
              weight === 'normal' && 'font-normal',
              weight === 'semibold' && 'font-semibold',
              weight === 'bold' && 'font-bold'
            )}
          >
            {formatDecimalPlaces(value, 1, 1)}
          </div>
        )}
      </div>
    );
  }

  const stars: React.ReactNode[] = [];
  for (let i = 1; i <= 5; i++) {
    const state: RatingStarProps['state'] =
      value >= i ? 'full' : value < i && value > i - 1 ? 'half' : 'empty';

    stars.push(<RatingStar key={i} className={clsx(starStyle)} state={state} />);
  }

  return (
    <div aria-hidden="true" className="flex gap-x-px">
      {stars}
    </div>
  );
}
